import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sanityFetch, sanityImageUrl } from "../sanityClient";
import kitchen_1 from "../assets/kitchen/kitchen-1.jpg";
import kitchen_2 from "../assets/kitchen/kitchen-2.jpg";
import kitchen_3 from "../assets/kitchen/Kitchen-3.jpg";
import kitchen_4 from "../assets/kitchen/kitchen-4.jpg";

interface KitchenRate {
  chefCapacity: number;
  forProfitHourly: number;
  nonProfitHourly: number;
}

interface SanityImage {
  asset: { _ref: string; _type: string };
  alt?: string;
}

interface CommercialKitchenSettings {
  rentalYear?: string;
  additionalStaffRate?: number;
  rentalRates?: KitchenRate[];
  images?: SanityImage[];
}

const FALLBACK_RATES: KitchenRate[] = [
  { chefCapacity: 2, forProfitHourly: 41, nonProfitHourly: 27.5 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.75rem",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2
      style={{
        fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: 56,
        height: 4,
        background: "linear-gradient(90deg, #6dc744, #4a8c25)",
        borderRadius: 2,
        marginTop: 8,
      }}
    />
  </div>
);

const NAV_ITEMS = [
  { label: "Gallery", href: "#gallery" },
  { label: "Types of Use", href: "#event-types" },
  { label: "Pricing", href: "#pricing" },
  { label: "Kitchen Info", href: "#kitchen-info" },
  { label: "Amenities", href: "#amenities" },
  { label: "Book Now", href: "#book" },
];

const FAQ_ITEMS = [
  {
    question: "What equipment does the kitchen come with?",
    answer:
      "Stoves, ovens, convection oven, commercial microwaves, fridges, ice machine, blender/mixer, counters, commercial dishwasher, and two double sinks — among other features.",
  },
  {
    question: "Do I need to bring my own pots and pans?",
    answer:
      "We have a basic stock but encourage you to bring small supplies or utensils. More items will be added gradually as operations establish themselves.",
  },
  {
    question: "Is there cold storage available?",
    answer:
      "There are fridges and freezers that renters can use during food prep, but they are not available for long-term storage between sessions.",
  },
  {
    question: "Is the kitchen accessible?",
    answer:
      "Yes — it is at-grade, with push-button doors and several accessible counters designed to accommodate all users.",
  },
  {
    question: "Can I rent it when other events are happening in the building?",
    answer:
      "Sometimes — not during Market Days. The kitchen connects by pass-through to the Workshop Room, so combined bookings are possible. Ask staff for details.",
  },
];

const KitchenDetails: React.FC = () => {
  const [settings, setSettings] = useState<CommercialKitchenSettings | null>(
    null,
  );
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    sanityFetch<CommercialKitchenSettings>(
      `*[_type == "commercialKitchen"][0]{rentalYear, additionalStaffRate, rentalRates, images[]}`,
    ).then((data) => {
      if (data) setSettings(data);
    });
  }, []);

  const rentalYear = settings?.rentalYear ?? "2026";
  const staffRate = settings?.additionalStaffRate ?? 18;
  const rates = settings?.rentalRates?.length
    ? settings.rentalRates
    : FALLBACK_RATES;

  const FALLBACK_IMAGES = [kitchen_1, kitchen_4, kitchen_2, kitchen_3];
  const images: string[] = settings?.images?.length
    ? settings.images.map((img) => sanityImageUrl(img.asset))
    : FALLBACK_IMAGES;

  return (
    <div style={{ fontFamily: "inherit" }}>
      {/* ── HERO ── */}
      <section
        style={{
          position: "relative",
          minHeight: 520,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <img
          src={images[0]}
          alt="Commercial Kitchen"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 50%",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(10,38,5,0.88) 0%, rgba(13,27,42,0.82) 55%, rgba(21,3,8,0.78) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: -120,
            right: -120,
            width: 440,
            height: 440,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(198,37,45,0.22) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -100,
            left: -100,
            width: 380,
            height: 380,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "5rem 1.5rem 6rem",
            position: "relative",
            zIndex: 1,
            width: "100%",
          }}
        >
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            style={{ maxWidth: 640 }}
          >
            <motion.span
              variants={fadeUp}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(193,154,59,0.15)",
                border: "1px solid rgba(193,154,59,0.4)",
                borderRadius: 999,
                padding: "0.35rem 1rem",
                color: "#C19A3B",
                fontSize: "0.82rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  background: "#C19A3B",
                  borderRadius: "50%",
                  display: "inline-block",
                }}
              />
              Rental Venue
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{
                fontSize: "clamp(2.4rem, 5.5vw, 4rem)",
                fontWeight: 900,
                color: "white",
                lineHeight: 1.08,
                marginBottom: "1.25rem",
              }}
            >
              Commercial{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #C19A3B, #c6252d)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Kitchen
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "1.15rem",
                color: "rgba(255,255,255,0.75)",
                lineHeight: 1.7,
                marginBottom: "2rem",
              }}
            >
              A fully equipped commercial kitchen for food prep, culinary
              workshops, and catering. At-grade, fully accessible, and connected
              by pass-through to the Workshop Room for seamless combined events.
            </motion.p>

            <motion.div
              variants={fadeUp}
              style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}
            >
              <a
                href="#book"
                style={{
                  display: "inline-block",
                  background: "linear-gradient(135deg, #c6252d, #a01e25)",
                  color: "white",
                  borderRadius: 999,
                  padding: "0.9rem 2.2rem",
                  fontWeight: 700,
                  fontSize: "1rem",
                  cursor: "pointer",
                  textDecoration: "none",
                  boxShadow: "0 4px 20px rgba(198,37,45,0.35)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 28px rgba(198,37,45,0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px rgba(198,37,45,0.35)";
                }}
              >
                Inquire About Rental →
              </a>
              <a
                href="#pricing"
                style={{
                  display: "inline-block",
                  background: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "white",
                  borderRadius: 999,
                  padding: "0.9rem 2.2rem",
                  fontWeight: 700,
                  fontSize: "1rem",
                  cursor: "pointer",
                  textDecoration: "none",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.18)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                }}
              >
                View Pricing
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── STICKY NAV ── */}
      <div
        style={{
          position: "sticky",
          top: 65,
          zIndex: 90,
          background: "rgba(255,253,248,0.95)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid rgba(45,106,20,0.12)",
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 1.5rem",
            display: "flex",
            gap: "0.25rem",
            overflowX: "auto",
            scrollbarWidth: "none",
          }}
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              style={{
                display: "inline-block",
                padding: "0.9rem 1.1rem",
                fontSize: "0.88rem",
                fontWeight: 600,
                color: "#2d6a14",
                textDecoration: "none",
                borderBottom: "2px solid transparent",
                whiteSpace: "nowrap",
                transition: "color 0.2s, border-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#c6252d";
                e.currentTarget.style.borderBottomColor = "#c6252d";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "#2d6a14";
                e.currentTarget.style.borderBottomColor = "transparent";
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>

      {/* ── PHOTO GALLERY ── */}
      <section
        id="gallery"
        style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <SectionHeading>Photo Gallery</SectionHeading>
            </motion.div>

            {isMobile ? (
              /* ── Mobile gallery: full-width hero + 2-column grid ── */
              <motion.div variants={fadeUp} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ width: "100%", aspectRatio: "16/9", overflow: "hidden", borderRadius: 12, cursor: "pointer" }} onClick={() => setLightboxImg(images[0])}>
                  <img src={images[0]} alt="Kitchen" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                  {images.slice(1).map((src, i) => (
                    <div key={i} style={{ aspectRatio: "4/3", overflow: "hidden", borderRadius: 12, cursor: "pointer" }} onClick={() => setLightboxImg(src)}>
                      <img src={src} alt={`Kitchen ${i + 2}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </motion.div>
            ) : (
              /* ── Desktop gallery: left hero + right stacked ── */
              <motion.div
                variants={fadeUp}
                style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gridTemplateRows: "280px 280px", gap: "0.75rem" }}
              >
                <div style={{ gridColumn: "1 / 2", gridRow: "1 / 3", overflow: "hidden", borderRadius: 16, cursor: "pointer" }} onClick={() => setLightboxImg(images[0])}>
                  <img src={images[0]} alt="Kitchen" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                    onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                    onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")} />
                </div>
                {images.slice(1).map((src, i) => (
                  <div key={i} style={{ overflow: "hidden", borderRadius: 16, cursor: "pointer" }} onClick={() => setLightboxImg(src)}>
                    <img src={src} alt={`Kitchen ${i + 2}`} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease" }}
                      onMouseOver={(e) => (e.currentTarget.style.transform = "scale(1.04)")}
                      onMouseOut={(e) => (e.currentTarget.style.transform = "scale(1)")} />
                  </div>
                ))}
              </motion.div>
            )}
            <motion.p
              variants={fadeUp}
              style={{
                color: "#6b7280",
                fontSize: "0.85rem",
                marginTop: "0.75rem",
                textAlign: "center",
              }}
            >
              Click any photo to enlarge
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          onClick={() => setLightboxImg(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.88)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxImg}
            alt="Enlarged"
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              borderRadius: 12,
              boxShadow: "0 8px 64px rgba(0,0,0,0.5)",
              objectFit: "contain",
            }}
          />
          <button
            onClick={() => setLightboxImg(null)}
            style={{
              position: "absolute",
              top: 20,
              right: 24,
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: "50%",
              width: 40,
              height: 40,
              color: "white",
              fontSize: "1.3rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── TYPES OF USE ── */}
      <section
        id="event-types"
        style={{ background: "#f2fbee", padding: "5rem 1.5rem", scrollMarginTop: 120 }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <SectionHeading>Types of Use</SectionHeading>
              <p
                style={{
                  color: "#4a5568",
                  fontSize: "1.05rem",
                  lineHeight: 1.7,
                  maxWidth: 640,
                  margin: "-1rem 0 2rem",
                }}
              >
                Our Commercial Kitchen is a versatile space designed for
                professional culinary work and community food programming.
              </p>
            </motion.div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "1.25rem",
              }}
            >
              {[
                {
                  icon: "🧑‍🍳",
                  title: "Food Product Prep",
                  desc: "Prepare food products for sale on-site at the market or for off-site distribution.",
                },
                {
                  icon: "👩‍🍳",
                  title: "Culinary Workshops",
                  desc: "Host cooking classes and demonstrations — connects by pass-through to the Workshop Room.",
                },
                {
                  icon: "🍱",
                  title: "Catering Production",
                  desc: "Use the fully equipped space to produce meals for catering events throughout the building.",
                },
                {
                  icon: "🥐",
                  title: "Baking & Pastry",
                  desc: "Convection oven and commercial equipment make it ideal for baking and specialty food prep.",
                },
                {
                  icon: "🧪",
                  title: "Recipe Development",
                  desc: "A professional environment for testing and developing new food products before scaling.",
                },
                {
                  icon: "✨",
                  title: "Something Unique?",
                  desc: "Have a different culinary idea? Contact our Events Manager to explore what's possible.",
                  link: "mailto:eventsmanager@sjfm.ca",
                },
              ].map(({ icon, title, desc, link }, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <LightCard
                    style={{ borderTop: "4px solid #C19A3B", height: "100%" }}
                  >
                    <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>
                      {icon}
                    </div>
                    <h3
                      style={{
                        margin: "0 0 0.5rem",
                        fontWeight: 800,
                        fontSize: "1rem",
                        color: "#0d1b2a",
                      }}
                    >
                      {title}
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        color: "#4a5568",
                        fontSize: "0.92rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {link ? (
                        <>
                          <span>{desc.split("Contact")[0]}</span>
                          <a
                            href={link}
                            style={{ color: "#c6252d", fontWeight: 600 }}
                          >
                            Contact our Events Manager
                          </a>
                        </>
                      ) : (
                        desc
                      )}
                    </p>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section
        id="pricing"
        style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <SectionHeading>{rentalYear} Rental Rates</SectionHeading>
              <p
                style={{
                  color: "#4a5568",
                  fontSize: "1.05rem",
                  lineHeight: 1.7,
                  maxWidth: 600,
                  margin: "-1rem 0 2rem",
                }}
              >
                Billed hourly. All rates include one staff member on-site. HST
                applies to all totals.
              </p>
            </motion.div>

            {/* Availability Warning */}
            <motion.div
              variants={fadeUp}
              style={{
                background:
                  "linear-gradient(135deg, rgba(193,154,59,0.1), rgba(138,109,40,0.07))",
                border: "1px solid rgba(193,154,59,0.4)",
                borderRadius: 14,
                padding: "1.25rem 1.5rem",
                marginBottom: "2rem",
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: "1.8rem", flexShrink: 0 }}>⚠️</span>
              <div>
                <p
                  style={{
                    margin: 0,
                    color: "#6b4f1a",
                    fontWeight: 700,
                    fontSize: "1rem",
                  }}
                >
                  Limited Availability
                </p>
                <p
                  style={{
                    margin: 0,
                    color: "#8a6d28",
                    fontSize: "0.92rem",
                    lineHeight: 1.5,
                  }}
                >
                  Times are limited — Market Vendors have priority access and
                  the kitchen is heavily booked. We recommend reaching out early
                  to check availability.
                </p>
              </div>
            </motion.div>

            {/* Rate cards */}
            <motion.div
              variants={fadeUp}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "1.25rem",
                marginBottom: "1.5rem",
              }}
            >
              {rates.map((rate, idx) => (
                <React.Fragment key={idx}>
                  {/* For-Profit card */}
                  <LightCard style={{ borderTop: "4px solid #c6252d" }}>
                    <p
                      style={{
                        margin: "0 0 0.4rem",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#c6252d",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                      }}
                    >
                      For-Profit / Business
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "0.4rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "2.4rem",
                          fontWeight: 900,
                          color: "#0d1b2a",
                        }}
                      >
                        ${rate.forProfitHourly}
                      </span>
                      <span
                        style={{
                          fontSize: "1rem",
                          color: "#6b7280",
                          fontWeight: 500,
                        }}
                      >
                        /hour
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "0.82rem",
                        color: "#2d6a14",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          background: "#6dc744",
                          borderRadius: "50%",
                          display: "inline-block",
                        }}
                      />
                      Up to {rate.chefCapacity} chef
                      {rate.chefCapacity !== 1 ? "s" : ""}
                    </span>
                  </LightCard>

                  {/* Non-Profit card */}
                  <LightCard style={{ borderTop: "4px solid #C19A3B" }}>
                    <p
                      style={{
                        margin: "0 0 0.4rem",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#8a6d28",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                      }}
                    >
                      Non-Profit / Community
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: "0.4rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "2.4rem",
                          fontWeight: 900,
                          color: "#0d1b2a",
                        }}
                      >
                        ${rate.nonProfitHourly}
                      </span>
                      <span
                        style={{
                          fontSize: "1rem",
                          color: "#6b7280",
                          fontWeight: 500,
                        }}
                      >
                        /hour
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: "0.82rem",
                        color: "#2d6a14",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          background: "#6dc744",
                          borderRadius: "50%",
                          display: "inline-block",
                        }}
                      />
                      Up to {rate.chefCapacity} chef
                      {rate.chefCapacity !== 1 ? "s" : ""}
                    </span>
                  </LightCard>
                </React.Fragment>
              ))}
            </motion.div>

            <motion.div
              variants={fadeUp}
              style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}
            >
              {[
                { label: "Billing", value: "Charged per hour" },
                {
                  label: "Staff included",
                  value: "1 SJFM staff member on-site",
                },
                {
                  label: "Additional staff",
                  value: `$${staffRate}/hr per person`,
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    background: "#f2fbee",
                    borderRadius: 12,
                    padding: "1rem 1.25rem",
                    border: "1px solid rgba(45,106,20,0.12)",
                    display: "flex",
                    gap: "0.75rem",
                    alignItems: "center",
                    flex: "1 1 200px",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      background: "#6dc744",
                      borderRadius: "50%",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: "#0d1b2a", fontSize: "0.92rem" }}>
                    <strong>{label}:</strong> {value}
                  </span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── KITCHEN INFO FAQ ── */}
      <section
        id="kitchen-info"
        style={{ background: "#f2fbee", padding: "5rem 1.5rem", scrollMarginTop: 120 }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <SectionHeading>Kitchen Information</SectionHeading>
              <p
                style={{
                  color: "#4a5568",
                  fontSize: "1.05rem",
                  lineHeight: 1.7,
                  margin: "-1rem 0 2rem",
                }}
              >
                Common questions about using our commercial kitchen.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {FAQ_ITEMS.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    background: "white",
                    borderRadius: 14,
                    border: "1px solid rgba(45,106,20,0.12)",
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                  }}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1.2rem 1.5rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      gap: "1rem",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#0d1b2a",
                        lineHeight: 1.5,
                      }}
                    >
                      {item.question}
                    </span>
                    <span
                      style={{
                        fontSize: "1.2rem",
                        color: "#C19A3B",
                        flexShrink: 0,
                        transition: "transform 0.25s",
                        transform:
                          openFaq === idx ? "rotate(45deg)" : "rotate(0deg)",
                      }}
                    >
                      +
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === idx && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <p
                          style={{
                            margin: 0,
                            padding: "0 1.5rem 1.25rem",
                            color: "#4a5568",
                            fontSize: "0.97rem",
                            lineHeight: 1.7,
                          }}
                        >
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── AMENITIES ── */}
      <section
        id="amenities"
        style={{ background: "#fffdf8", padding: "5rem 1.5rem", scrollMarginTop: 120 }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp}>
              <SectionHeading>Kitchen Equipment & Amenities</SectionHeading>
            </motion.div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1.1rem",
              }}
            >
              {[
                {
                  icon: "🔥",
                  title: "Stoves & Ovens",
                  desc: "Full commercial stoves, standard ovens, and a convection oven for all cooking needs.",
                },
                {
                  icon: "❄️",
                  title: "Refrigeration",
                  desc: "Commercial fridges and freezers available during your session for food prep and chilling.",
                },
                {
                  icon: "🧊",
                  title: "Ice Machine",
                  desc: "On-site ice machine for food prep and cooling requirements.",
                },
                {
                  icon: "🍽️",
                  title: "Commercial Dishwasher",
                  desc: "High-capacity commercial dishwasher and two double sinks for easy cleanup.",
                },
                {
                  icon: "⚡",
                  title: "Appliances",
                  desc: "Commercial microwaves, blender, and mixer available for use.",
                },
                {
                  icon: "🔗",
                  title: "Workshop Room Pass-Through",
                  desc: "Connected by pass-through to the Workshop Room — ideal for combined cooking class events.",
                },
                {
                  icon: "♿",
                  title: "Fully Accessible",
                  desc: "At-grade entrance, push-button doors, and accessible counters throughout.",
                },
                {
                  icon: "📶",
                  title: "Free Wi-Fi",
                  desc: "Public Wi-Fi available at no cost throughout the building.",
                },
                {
                  icon: "🪣",
                  title: "Ample Workspace",
                  desc: "Generous counter space and storage areas to support your food preparation workflow.",
                },
              ].map(({ icon, title, desc }, idx) => (
                <motion.div key={idx} variants={fadeUp}>
                  <LightCard
                    style={{
                      display: "flex",
                      gap: "1rem",
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "1.75rem",
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      {icon}
                    </span>
                    <div>
                      <h3
                        style={{
                          margin: "0 0 0.3rem",
                          fontWeight: 800,
                          fontSize: "0.95rem",
                          color: "#0d1b2a",
                        }}
                      >
                        {title}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          color: "#4a5568",
                          fontSize: "0.88rem",
                          lineHeight: 1.6,
                        }}
                      >
                        {desc}
                      </p>
                    </div>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── STAFF & BOOKING CTA ── */}
      <section
        id="book"
        style={{
          background:
            "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem",
          position: "relative",
          overflow: "hidden",
          scrollMarginTop: 120,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -80,
            left: -80,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div
              variants={fadeUp}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "3rem",
                alignItems: "start",
              }}
            >
              {/* Staff info */}
              <div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(109,199,68,0.12)",
                    border: "1px solid rgba(109,199,68,0.3)",
                    borderRadius: 999,
                    padding: "0.35rem 1rem",
                    color: "#6dc744",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: "1.25rem",
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      background: "#6dc744",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  Staff Support
                </span>
                <h2
                  style={{
                    fontSize: "clamp(1.5rem, 3vw, 2rem)",
                    fontWeight: 900,
                    color: "white",
                    margin: "0 0 1.25rem",
                  }}
                >
                  We're Here to Help
                </h2>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.9rem",
                  }}
                >
                  {[
                    {
                      label: "Included",
                      text: "All rentals include one SJFM staff member on-site for the duration of your session.",
                    },
                    {
                      label: "Extra Staff",
                      text: "Additional staff may be required depending on the nature of your event.",
                    },
                    {
                      label: "Staffing Rate",
                      text: `Extra staff are billed at $${staffRate}/hr per person.`,
                    },
                  ].map(({ label, text }) => (
                    <div
                      key={label}
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          background: "#6dc744",
                          borderRadius: "50%",
                          flexShrink: 0,
                          marginTop: 7,
                        }}
                      />
                      <p
                        style={{
                          margin: 0,
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.65,
                          fontSize: "0.97rem",
                        }}
                      >
                        <strong style={{ color: "white" }}>{label}:</strong>{" "}
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Booking card */}
              <div
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 20,
                  padding: "2.5rem",
                  backdropFilter: "blur(8px)",
                }}
              >
                <h2
                  style={{
                    fontSize: "1.7rem",
                    fontWeight: 900,
                    color: "white",
                    margin: "0 0 0.75rem",
                  }}
                >
                  Ready to Book?
                </h2>
                <p
                  style={{
                    color: "rgba(255,255,255,0.72)",
                    lineHeight: 1.7,
                    marginBottom: "2rem",
                    fontSize: "1rem",
                  }}
                >
                  Reach out to our Events Manager to check availability, discuss
                  your needs, and confirm a time that works for you.
                </p>
                <a
                  href="mailto:eventsmanager@sjfm.ca"
                  style={{
                    display: "block",
                    textAlign: "center",
                    background: "linear-gradient(135deg, #c6252d, #a01e25)",
                    color: "white",
                    borderRadius: 999,
                    padding: "1rem 2rem",
                    fontWeight: 700,
                    fontSize: "1rem",
                    textDecoration: "none",
                    boxShadow: "0 4px 20px rgba(198,37,45,0.4)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    marginBottom: "1rem",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 28px rgba(198,37,45,0.55)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 20px rgba(198,37,45,0.4)";
                  }}
                >
                  eventsmanager@sjfm.ca →
                </a>
                <p
                  style={{
                    margin: 0,
                    textAlign: "center",
                    color: "rgba(255,255,255,0.45)",
                    fontSize: "0.82rem",
                  }}
                >
                  We typically respond within 1–2 business days
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default KitchenDetails;
